import React from "react";
import { Checkbox } from "antd";
import styled from "styled-components";
import type { CountryTaxConfig } from "@/api/countryTaxConfig";
import { AppSelect, FormField } from "@/shared/components";
import { phoneCountries } from "@/shared/utils/phoneCountries";
import { rateLine, type TaxDetailsErrors, type TaxDetailsValues } from "../utils/taxDetails";

interface Props {
	idPrefix: string;
	values: TaxDetailsValues;
	errors: TaxDetailsErrors;
	config: CountryTaxConfig | null;
	configLoading: boolean;
	countryName: string;
	onChange: <K extends keyof TaxDetailsValues>(field: K, value: TaxDetailsValues[K]) => void;
	/** Hide the country select when the host already fixes the country. */
	showCountry?: boolean;
	/** Last four of a registration number already on file, so re-entry is optional until the creator changes it. */
	existingNumberLast4?: string | null;
	disabled?: boolean;
	popupClassName?: string;
}

/** Singapore first (16 Sep), then the rest of the list. */
const countryOptions = (() => {
	const sg = phoneCountries.find((c) => c.iso === "SG");
	const rest = phoneCountries.filter((c) => c.iso !== "SG");
	return [...(sg ? [sg] : []), ...rest].map((c) => ({ value: c.iso, label: c.name }));
})();

const ENTITY_OPTIONS = [
	{ value: "individual", label: "Individual (sole trader)" },
	{ value: "company", label: "Company" },
];

/**
 * The creator's tax declaration fields (Ticket-348 §10). There is deliberately no rate input: the
 * statement records the country rate in force on the day it is issued.
 */
export const TaxDetailsFields: React.FC<Props> = ({
	idPrefix,
	values,
	errors,
	config,
	configLoading,
	countryName,
	onChange,
	showCountry = true,
	existingNumberLast4 = null,
	disabled = false,
	popupClassName,
}) => {
	const taxName = config?.taxName ?? "tax";
	const label = config?.registrationNumberLabel ?? "Tax registration number";
	return (
		<Grid>
			{showCountry ? (
				<Field
					$wide
					htmlFor={`${idPrefix}-country`}
					label="Country of tax residence"
					error={errors.countryCode}
					required
				>
					<Select
						id={`${idPrefix}-country`}
						showSearch
						optionFilterProp="label"
						getPopupContainer={() => document.body}
						popupClassName={popupClassName}
						disabled={disabled}
						value={values.countryCode || undefined}
						onChange={(value) => onChange("countryCode", String(value))}
						options={countryOptions}
					/>
				</Field>
			) : null}
			<Field htmlFor={`${idPrefix}-entity`} label="I am paid as" error={errors.entityType} required>
				<Select
					id={`${idPrefix}-entity`}
					getPopupContainer={() => document.body}
					popupClassName={popupClassName}
					disabled={disabled}
					value={values.entityType || undefined}
					onChange={(value) => onChange("entityType", value as TaxDetailsValues["entityType"])}
					options={ENTITY_OPTIONS}
				/>
			</Field>
			<Field htmlFor={`${idPrefix}-legal-name`} label="Legal name" error={errors.legalName} required>
				<Input
					id={`${idPrefix}-legal-name`}
					value={values.legalName}
					disabled={disabled}
					placeholder={values.entityType === "company" ? "Registered company name" : "Full legal name"}
					onChange={(event) => onChange("legalName", event.target.value)}
				/>
			</Field>
			<Wide>
				<Checkbox
					id={`${idPrefix}-registered`}
					checked={values.isTaxRegistered}
					disabled={disabled}
					onChange={(event) => onChange("isTaxRegistered", event.target.checked)}
				>
					I am registered for {taxName}
					{config?.taxAuthorityName && config.taxAuthorityName !== "N/A"
						? ` with the ${config.taxAuthorityName}`
						: ""}
				</Checkbox>
			</Wide>
			{values.isTaxRegistered ? (
				<Field
					$wide
					htmlFor={`${idPrefix}-registration-number`}
					label={label}
					error={errors.registrationNumber}
					required={values.countryCode !== "US"}
				>
					<Input
						id={`${idPrefix}-registration-number`}
						value={values.registrationNumber}
						disabled={disabled}
						autoComplete="off"
						placeholder={
							existingNumberLast4
								? `On file, ending ${existingNumberLast4}. Enter the full number to change it.`
								: label
						}
						onChange={(event) => onChange("registrationNumber", event.target.value)}
					/>
				</Field>
			) : null}
			<RateLine aria-live="polite">
				{configLoading ? "Checking the current rate…" : rateLine(config, countryName)}
			</RateLine>
			<Note>
				Declare the country where you are registered for tax. If you live or work away from your country of tax
				residence, local tax may not apply to you; your Payment Statement uses the declaration above.
			</Note>
		</Grid>
	);
};

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 24px;
	@media (max-width: 860px) {
		grid-template-columns: 1fr;
	}
`;
const Wide = styled.div`
	grid-column: 1 / -1;
	color: #303030;
	font-size: 14px;
`;
const Field = styled(FormField)<{ $wide?: boolean }>`
	min-width: 0;
	grid-column: ${({ $wide }) => ($wide ? "1 / -1" : "auto")};
`;
const Input = styled.input`
	width: 100%;
	height: 40px;
	padding: 8px 12px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	outline: none;
	background: #fff;
	color: #171717;
	font: inherit;
	font-size: 16px;
	&:focus {
		border-color: #9fadf1;
	}
	&:disabled {
		background: #f5f5f5;
	}
`;
const Select = styled(AppSelect)`
	width: 100%;
	height: 40px;
	.ant-select-selector {
		height: 40px !important;
		border-color: #cdd4f6 !important;
		border-radius: 8px !important;
	}
	.ant-select-selection-item,
	.ant-select-selection-placeholder {
		font-size: 16px !important;
		line-height: 38px !important;
	}
`;
const RateLine = styled.p`
	grid-column: 1 / -1;
	margin: 0;
	padding: 12px 16px;
	border-radius: 12px;
	background: #f3f5ff;
	border: 1px solid #cdd4f6;
	color: #303030;
	font-size: 14px;
	line-height: 1.5;
`;
const Note = styled.p`
	grid-column: 1 / -1;
	margin: 0;
	color: #707070;
	font-size: 12px;
	line-height: 1.5;
`;
